/*
 * Scene composer — wires every renderer into the right z-order and supports
 * zone swaps (TASK-07).
 *
 * Z-order (bottom → top):
 *   sky → square backdrop (well, houses, trees, bench, lanterns, mushroom field)
 *       OR apothecary backdrop
 *   → critters (y-sorted)
 *   → fireflies → shooting stars → weather → float text → vignette
 *
 * Zone swap: the square and apothecary backdrops are both mounted; visibility
 * toggles via `setZone(zoneId)`. The apothecary's own sky gradient fully covers
 * the animated sky/stars layer when active, so we don't need to hide the sky
 * to keep both backdrops mutually exclusive.
 */

import { Container, Graphics } from 'pixi.js';
import type { AnyCritter, FishState, FloatText, ForageMushroom } from '@/state/types';
import { WORLD } from '@/render/palette';
import { createSky } from '@/render/sky';
import { createWell } from '@/render/well';
import { createMushroomHouse } from '@/render/house';
import { createTree } from '@/render/tree';
import { createLantern, type LanternView } from '@/render/lantern';
import { createFireflies } from '@/render/firefly';
import { createMushroomField } from '@/render/mushroom';
import { createFloatTextLayer } from '@/render/floatText';
import { createCritterView, type CritterView } from '@/render/critter';
import { createWeather, weatherWindX } from '@/render/weather';
import { createBench } from '@/render/bench';
import { createShootingStars, type ShootingStarsView } from '@/render/stargaze';
import { createPortal } from '@/render/portal';
import { createApothecaryBackdrop } from '@/render/zones/apothecary';
import { currentWorldState, lanternIntensityFor, fireflyCountFactorFor } from '@/state/world';
import { getCurrentZone, portalFor, type ZoneId } from '@/state/zone';

export interface SceneView {
  container: Container;
  addCritter(c: AnyCritter): CritterView;
  removeCritter(id: string): void;
  spawnFloatText(text: string, x: number, y: number, color: string): void;
  /** Shooting-stars layer — game.ts drives it during the stargaze minigame. */
  stargaze: ShootingStarsView;
  /** Swap visible zone. Backdrops are both mounted; visibility toggles. */
  setZone(zone: ZoneId): void;
  update(t: number, dt: number, ctx: SceneFrameContext): void;
}

export interface SceneFrameContext {
  critters: readonly AnyCritter[];
  forageMushrooms: readonly ForageMushroom[];
  fish: FishState;
  floatTextsToSpawn?: FloatText[];
}

export function createScene(): SceneView {
  const root = new Container();

  // Always-on ambient layers.
  const sky = createSky();
  const fireflies = createFireflies();
  const floatTextLayer = createFloatTextLayer();
  const weather = createWeather();
  const stargaze = createShootingStars();

  // --- Square backdrop ---
  const squareBackdrop = new Container();
  const well = createWell();
  const mushroomField = createMushroomField();

  const houses = new Container();
  houses.addChild(createMushroomHouse(85, 290, 0xc9514c));
  houses.addChild(createMushroomHouse(515, 320, 0xd4a04f));
  houses.addChild(createMushroomHouse(120, 640, 0x8a5a9a));
  houses.addChild(createMushroomHouse(490, 670, 0x4f8a9a));

  const trees = new Container();
  trees.addChild(createTree(200, 220));
  trees.addChild(createTree(420, 240));
  trees.addChild(createTree(50, 510));
  trees.addChild(createTree(560, 530));

  const bench = createBench(475, 470);
  trees.addChild(bench);

  const lanterns: LanternView[] = [];
  const lanternLayer = new Container();
  for (const [lx, ly] of [
    [180, 400],
    [420, 400],
    [230, 580],
    [380, 580],
  ] as const) {
    const l = createLantern(lx, ly);
    lanterns.push(l);
    lanternLayer.addChild(l.container);
  }

  // Square portal — south edge, leads to the apothecary.
  const squarePortalCfg = portalFor('square');
  const squarePortal = createPortal(squarePortalCfg.x, squarePortalCfg.y);

  squareBackdrop.addChild(well.container);
  squareBackdrop.addChild(houses);
  squareBackdrop.addChild(trees);
  squareBackdrop.addChild(lanternLayer);
  squareBackdrop.addChild(mushroomField.container);
  squareBackdrop.addChild(squarePortal);

  // --- Apothecary backdrop ---
  const apothecaryBackdrop = createApothecaryBackdrop();
  const apothecaryPortalCfg = portalFor('apothecary');
  apothecaryBackdrop.addChild(createPortal(apothecaryPortalCfg.x, apothecaryPortalCfg.y));

  const crittersLayer = new Container();

  // Vignette — painted once.
  const vignette = new Graphics();
  vignette.rect(0, 0, WORLD.width, WORLD.height).fill({ color: 0x000000, alpha: 0 });
  vignette.rect(0, 0, WORLD.width, 30).fill({ color: 0x000000, alpha: 0.35 });
  vignette
    .rect(0, WORLD.height - 30, WORLD.width, 30)
    .fill({ color: 0x000000, alpha: 0.45 });
  vignette.rect(0, 0, 30, WORLD.height).fill({ color: 0x000000, alpha: 0.3 });
  vignette
    .rect(WORLD.width - 30, 0, 30, WORLD.height)
    .fill({ color: 0x000000, alpha: 0.3 });

  // Assemble (z-order matters).
  root.addChild(sky.container);
  root.addChild(squareBackdrop);
  root.addChild(apothecaryBackdrop);
  root.addChild(crittersLayer);
  root.addChild(fireflies.container);
  root.addChild(stargaze.container);
  root.addChild(weather.container);
  root.addChild(floatTextLayer.container);
  root.addChild(vignette);

  // Initial zone visibility.
  let activeZone: ZoneId = getCurrentZone();
  applyZoneVisibility(activeZone);

  function applyZoneVisibility(zone: ZoneId): void {
    squareBackdrop.visible = zone === 'square';
    apothecaryBackdrop.visible = zone === 'apothecary';
  }

  function setZone(zone: ZoneId): void {
    activeZone = zone;
    applyZoneVisibility(zone);
  }

  // Critter views indexed by id.
  const views = new Map<string, CritterView>();

  function addCritter(c: AnyCritter): CritterView {
    const v = createCritterView();
    views.set(c.id, v);
    crittersLayer.addChild(v.container);
    return v;
  }

  function removeCritter(id: string): void {
    const v = views.get(id);
    if (!v) return;
    crittersLayer.removeChild(v.container);
    v.container.destroy({ children: true });
    views.delete(id);
  }

  function spawnFloatText(text: string, x: number, y: number, color: string): void {
    floatTextLayer.spawn(text, x, y, color);
  }

  function update(t: number, dt: number, frame: SceneFrameContext): void {
    const world = currentWorldState();

    sky.update(t);

    // Square-only updates: only tick if the player is here.
    if (activeZone === 'square') {
      well.update(t, frame.fish);
      const intensity = lanternIntensityFor(world);
      for (const l of lanterns) l.update(t, intensity);
      mushroomField.container.visible = frame.forageMushrooms.length > 0;
      mushroomField.draw(frame.forageMushrooms);
    } else {
      mushroomField.container.visible = false;
    }

    const flyFactor = fireflyCountFactorFor(world);
    const wind = weatherWindX(world);
    fireflies.update(dt, flyFactor, wind);

    weather.update(dt, world);

    stargaze.update(dt);

    // Critter views.
    for (const c of frame.critters) {
      let v = views.get(c.id);
      if (!v) v = addCritter(c);
      v.update(c, t);
    }

    // Y-sort critters for fake depth.
    crittersLayer.children.sort((a, b) => a.position.y - b.position.y);

    floatTextLayer.update(dt);
  }

  return {
    container: root,
    addCritter,
    removeCritter,
    spawnFloatText,
    stargaze,
    setZone,
    update,
  };
}
